import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function StrategyManagement() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Strategy & Management Services | Synthera Security</title>

        <meta
          name="description"
          content="vCISO, cyber security strategy, risk management, security governance, and executive advisory services designed to improve security maturity and business resilience."
        />

        <meta
          name="keywords"
          content="vCISO, Cyber Security Strategy, Security Management, Risk Management, Security Governance, NIST CSF, Security Consulting, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Strategy & Management Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="Executive cyber security leadership, strategy, governance, and risk management services from Synthera Security."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/strategy"
        />

        <meta property="og:type" content="website" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/strategy"
        />
      </Helmet>

      <section className="bg-[#032d28] text-white relative overflow-hidden flex flex-col items-center py-24 px-6 min-h-screen">
        {/* BACK BUTTON */}
        <Link
          to="/services/security-consulting-advisory"
          className="absolute top-20 left-8 text-white/40 hover:text-[#24ff81] transition-colors"
        >
          <ArrowLeft size={26} />
        </Link>

        <div className="max-w-3xl mx-auto text-left w-full">
          {/* IMAGE */}
          <div className="flex justify-center mb-10">
            <img
              src="https://static.wixstatic.com/media/nsplsh_0fd8caba5f194480baf48fdca0834f31~mv2.jpg"
              alt="Strategy and Management"
              className="w-44 h-44 rounded-full object-cover shadow-xl"
            />
          </div>

          {/* TITLE */}
          <h1 className="text-3xl md:text-4xl font-bold tracking-wide mb-6 uppercase text-center">
            Strategy and Management
          </h1>

          {/* INTRO */}
          <p className="text-white/80 leading-relaxed mb-10">
            Effective <b>security management</b> begins at the top and is driven
            by a proactive, risk informed culture. Our Strategy and Management
            service, led by a{" "}
            <b>virtual Chief Information Security Officer (vCISO)</b>, can be
            tailored to any organisation's requirements, large or small. Our
            focus is maximising performance improvement while optimising return
            on investment from your cyber security budget.
          </p>

          {/* BENEFITS */}
          <h2 className="text-xl font-bold mb-4">
            The benefits of engaging a vCISO are:
          </h2>

          <div className="space-y-4 text-white/80 mb-12">
            <p>
              Access to a , <b>broad range of skillsets</b> within a single
              engagement
            </p>
            <p>
              <b>Burst capacity</b> for when you need it most
            </p>
            <p>
              Global expertise and{" "}
              <b>extensive security management experience</b> at highly
              competitive rates
            </p>
            <p>
              <b>Fast-tracking the maturity</b> of your cyber security culture,
              through wholistic security improvement covering the people,
              process and technology domains.
            </p>
          </div>

          {/* APPROACH */}
          <h2 className="text-xl font-bold mb-4">Our Approach</h2>
          <p className="text-white/80 leading-relaxed mb-12">
            Our approach is to provide our clients with a single executive
            resource as the point of contact and leader who can leverage the
            collective skillsets of our consulting pool to offer the
            capabilities of an entire cyber security team in a single
            engagement. There is no B-Team at Phronesis, so you can rest assured
            our strategies are time-tested and led by seasoned professionals
            with the experience to know what works and what doesn’t.
          </p>

          {/* EXPERIENCE */}
          <h2 className="text-xl font-bold mb-4">Our Experience</h2>
          <p className="text-white/80 leading-relaxed mb-12">
            We have provided executive support services for organisations around
            the world covering a broad range of industries, such as critical
            infrastructure, retail, shipping, oil and gas, and technology and
            software development. Our vCISO service is grounded on understanding
            what your organisation needs to thrive, and driving cyber security
            maturity improvement through consultation, education, and impeccable
            service delivery.
          </p>

          {/* KEY SERVICES */}
          <h2 className="text-xl font-bold mb-4">Key Services</h2>
          <p className="text-white/80 mb-4">
            Our most utilised services during a vCISO engagement are:
          </p>

          <ul className="list-disc pl-6 space-y-3 text-white/80 mb-14">
            <li>
              <b>Organisation-wide threat modelling and risk management</b>, to
              ensure the full spectrum of threats and risks are identified and
              managed across your organisation.
            </li>

            <li>
              <b>Security strategy definition and delivery</b>, to ensure your
              organisation is achieving its cyber security objectives.
            </li>

            <li>
              <b>Security maturity roadmapping</b>, benchmarked against the
              NIST-CSF to provide clarity of vision and cost effective control
              selection.
            </li>

            <li>
              <b>Security management and reporting</b>, including
              program/project management, resourcing support, recruitment
              vetting, and performance metrics reporting to the board and
              C-Suite.
            </li>

            <li>
              <b>Budget analysis and planning</b>, including Annualized Loss
              Expectancy and Mitigation Ratio assessments.
            </li>
          </ul>

          {/* CTA */}
          <p className="text-white/80 mb-8">
            To learn more about getting the most value and impact from your
            cyber security budget, get in touch today!
          </p>

          <div className="flex justify-center">
            <Link
              to="/contact"
              className="border border-white px-8 py-3 uppercase text-sm tracking-widest hover:bg-white hover:text-[#032d28] transition no-underline"
            >
              Find Out More
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
